import { forEach } from 'lodash';
import {
  RECIPE_BUILDER_SET_RECIPE_GROUPS,
  RECIPE_BUILDER_RESET_RECIPE_GROUPS,
  RECIPE_BUILDER_SET_EXPANDED_RECIPE_GROUP,
  RECIPE_BUILDER_SET_EXPANDED_RECIPE,
  RECIPE_BUILDER_RECIPE_APPLIED,
  RECIPE_BUILDER_SELECT_ATTR,
  RECIPE_BUILDER_SELECT_ALL_ATTRS,
  RECIPE_BUILDER_DESELECT_ATTR,
  RECIPE_BUILDER_DESELECT_ALL_ATTRS,
  RECIPE_BUILDER_APPLY_SCHEMA_SPEC,
  LOGOUT_COMPLETE,
  UPDATE_DESTINATION_DRAFT,
  ACCUMULATE_SCHEMA_FOR_NEXSET_SAMPLE,
  UPDATE_NEXSET_SAMPLE_DRAFT,
  CLEAR_NEXSET_SAMPLE_DRAFT,
  ACCUMULATE_SCHEMA,
  RECIPE_BUILDER_SET_IS_ASYNC,
  RECIPE_BUILDER_SET_PARSING_ERRORS,
  RECIPE_BUILDER_SET_LEGACY_MODE,
} from 'js/constants/actionTypes';
import DataSchemaProcessor from 'js/lib/DataSchemaProcessor';
import { setError } from 'js/actions/request';

const initialState = {
  recipeGroups: [],
  expandedRecipeGroupIndex: -1,
  expandedRecipeIndex: -1,
  isRecipeUnapplied: false, // whether the recipe has changed since it was last applied
  autoApply: false,
  isAsync: false,
  parsingErrors: {},
  selectedAttrs: {}, // selectedAttrs[gridId][attrName] = true
  appliedSchemaSpecId: null,
  destinationDraft: null,
  nexsetSampleDraft: null,
  isLegacyMode: false,

  ////// UNUSED //////
  //samples
  fromSamples: {
    updatedAt: null,
    flat: null,
    nested: null,
    byAttrId: null,
    byMetaAttrId: null,
  },
  toSamples: {
    updatedAt: null,
    flat: null,
    nested: null,
    byAttrId: null,
    byMetaAttrId: null,
  },

  //schema
  fromSchema: {
    flat: [],
    byAttrId: {},
  },
  toSchema: {
    flat: [],
    byAttrId: {},
  },
};

export const recipeBuilderReducer = (state = initialState, action) => {
  switch (action.type) {
    case RECIPE_BUILDER_SET_RECIPE_GROUPS:
      return setRecipeGroups(state, action);

    case RECIPE_BUILDER_RESET_RECIPE_GROUPS:
      return resetRecipeGroups(state, action);

    case RECIPE_BUILDER_SET_EXPANDED_RECIPE_GROUP:
      return setExpandedRecipeGroup(state, action);

    case RECIPE_BUILDER_SET_EXPANDED_RECIPE:
      return setExpandedRecipe(state, action);

    case RECIPE_BUILDER_RECIPE_APPLIED:
      return recipeApplied(state, action);

    case RECIPE_BUILDER_SELECT_ATTR:
      return selectRecipeBuilderAttr(state, action);

    case RECIPE_BUILDER_SELECT_ALL_ATTRS:
      return selectAllRecipeBuilderAttrs(state, action);

    case RECIPE_BUILDER_DESELECT_ATTR:
      return deselectRecipeBuilderAttr(state, action);

    case RECIPE_BUILDER_DESELECT_ALL_ATTRS:
      return deselectAllRecipeBuilderAttrs(state, action);

    case RECIPE_BUILDER_APPLY_SCHEMA_SPEC:
      return applySchemaSpec(state, action);

    case LOGOUT_COMPLETE:
      return initialState;

    case UPDATE_DESTINATION_DRAFT:
      return updateDestinationDraft(state, action);

    case ACCUMULATE_SCHEMA:
      return accumulateSchemaToDestinationDraft(state, action);

    case ACCUMULATE_SCHEMA_FOR_NEXSET_SAMPLE:
      return accumulateSchemaToNexsetSampleDraft(state, action);

    case UPDATE_NEXSET_SAMPLE_DRAFT:
      return updateNexsetSampleDraft(state, action);

    case CLEAR_NEXSET_SAMPLE_DRAFT:
      return clearNexsetSampleDraft(state, action);

    case RECIPE_BUILDER_SET_IS_ASYNC:
      return setRecipeBuilderAsync(state, action);

    case RECIPE_BUILDER_SET_PARSING_ERRORS:
      return setRecipeBuilderParsingErrors(state, action);

    case RECIPE_BUILDER_SET_LEGACY_MODE:
      return setLegacyMode(state, action);

    default:
      return state;
  }
};

function setRecipeGroups(state, action) {
  const { recipeGroups, isRecipeUnapplied, autoApply } = action.payload;

  return {
    ...state,
    recipeGroups,
    isRecipeUnapplied,
    autoApply,
  };
}

function resetRecipeGroups(state, action) {
  return initialState;
}

function setExpandedRecipeGroup(state, action) {
  const { index } = action.payload;

  return {
    ...state,
    expandedRecipeGroupIndex: index,
  };
}

function setExpandedRecipe(state, action) {
  const { index } = action.payload;

  return {
    ...state,
    expandedRecipeIndex: index,
  };
}

function recipeApplied(state, action) {
  return {
    ...state,
    autoApply: false,
    isRecipeUnapplied: false,
  };
}

function selectRecipeBuilderAttr(state, action) {
  const { attrName, gridId } = action.payload;
  const { selectedAttrs } = state;

  return {
    ...state,
    selectedAttrs: {
      // selection in one grid should clear selection in all others
      [gridId]: {
        ...selectedAttrs[gridId],
        [attrName]: true,
      },
    },
  };
}

function selectAllRecipeBuilderAttrs(state, action) {
  const { schema, gridId } = action.payload;
  const newSelectedAttrs = {};

  forEach(schema, ({ __name }) => {
    newSelectedAttrs[__name] = true;
  });

  return {
    ...state,
    selectedAttrs: {
      // selection in one grid should clear selection in all others
      [gridId]: newSelectedAttrs,
    },
  };
}

function deselectRecipeBuilderAttr(state, action) {
  const { attrName, gridId } = action.payload;
  const { selectedAttrs } = state;
  const newSelectedAttrs = {
    ...selectedAttrs,
    [gridId]: { ...selectedAttrs[gridId] },
  };
  delete newSelectedAttrs[gridId][attrName];

  return {
    ...state,
    selectedAttrs: newSelectedAttrs,
  };
}

function deselectAllRecipeBuilderAttrs(state, action) {
  return {
    ...state,
    selectedAttrs: initialState.selectedAttrs,
  };
}

function applySchemaSpec(state, action) {
  const { id } = action.payload;

  return {
    ...state,
    appliedSchemaSpecId: id,
  };
}

function updateDestinationDraft(state, action) {
  return {
    ...state,
    destinationDraft: {
      ...state.destinationDraft,
      ...action.payload.destinationDraft,
    },
  };
}

function accumulateSchemaToDestinationDraft(state, action) {
  const { schemas } = action?.payload?.data?.result;

  if (!schemas) {
    setError(
      'No schema found in the response. Please check the samples payload and try again.',
      true,
    );
  }

  const flattenSchema = DataSchemaProcessor.flattenSchema(schemas[0].properties, [], 0);

  return {
    ...state,
    destinationDraft: {
      ...state.destinationDraft,
      rawSchema: schemas[0],
      schema: flattenSchema,
    },
  };
}

function setRecipeBuilderAsync(state, action) {
  return {
    ...state,
    isAsync: action.payload.isAsync,
  };
}

function setRecipeBuilderParsingErrors(state, action) {
  return {
    ...state,
    parsingErrors: action.payload.parsingErrors,
  };
}

function setLegacyMode(state, action) {
  const { isLegacyMode } = action.payload;

  return {
    ...state,
    isLegacyMode,
  };
}

function accumulateSchemaToNexsetSampleDraft(state, action) {
  const { schemas } = action?.payload?.data?.result;

  if (!schemas) {
    setError(
      'No schema found in the response. Please check the samples payload and try again.',
      true,
    );
  }

  const flattenSchema = DataSchemaProcessor.flattenSchema(schemas[0].properties, [], 0);

  return {
    ...state,
    nexsetSampleDraft: {
      ...state.nexsetSampleDraft,
      rawSchema: schemas[0],
      schema: flattenSchema,
    },
  };
}

function updateNexsetSampleDraft(state, action) {
  return {
    ...state,
    nexsetSampleDraft: {
      ...state.nexsetSampleDraft,
      ...action.payload.nexsetSampleDraft,
    },
  };
}

function clearNexsetSampleDraft(state, action) {
  return {
    ...state,
    nexsetSampleDraft: null,
  };
}
